import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { Result } from '@cplug/frontend-core/utils'
import { ErrorCodes, type BaseError } from '@cplug/frontend-core/models'
import { CPStagingAccountActivationGateway } from '../gateways/CPStagingAccountActivationGateway'
import type { StagingAccountActivation } from '../models/StagingAccountActivation'

const useStagingAccountActivationStore = defineStore('shared/staging-account-activation', () => {
  const gateway = CPStagingAccountActivationGateway.instance()
  const activation = ref<StagingAccountActivation | undefined>()
  const loading = ref(false)
  const errors = ref<BaseError[]>([])

  const shouldShowBanner = computed(
    () => activation.value?.isAvailable === true && !activation.value.isActivated
  )

  async function request (call: () => Promise<Result<StagingAccountActivation>>): Promise<boolean> {
    loading.value = true
    const wrapped = await Result.ofPromise(call())
    loading.value = false

    if (wrapped.isError()) {
      errors.value = [wrapped.getError() ?? { code: ErrorCodes.Unknown }]
      return false
    }

    const result = wrapped.getValue()

    if (result == null || result.isError()) {
      errors.value = [result?.getError() ?? { code: ErrorCodes.Unknown }]
      return false
    }

    activation.value = result.getValue()
    errors.value = []
    return true
  }

  async function getStatus (): Promise<boolean> {
    if (activation.value != null) {
      return true
    }

    return await request(async () => await gateway.getStatus())
  }

  async function activate (): Promise<boolean> {
    if (loading.value) {
      return false
    }

    return await request(async () => await gateway.activate())
  }

  function $reset (): void {
    activation.value = undefined
    loading.value = false
    errors.value = []
  }

  return { activation, loading, errors, shouldShowBanner, getStatus, activate, $reset }
})

export default useStagingAccountActivationStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useStagingAccountActivationStore, (import.meta as any).hot))
}
